// draft-producer-runner.ts (Story 7.8) — the MAIN-THREAD wiring around the pure `draft-producer`.
// Supplies the things the core deliberately takes via injection: the npm Dexie `db` singleton, the
// online save transport (`apiFetch` → `POST …/save-draft`), the drain trigger (7.4's
// `triggerMainThreadDrain`), the clock, and the id minter. Mirrors `drain-runner.ts`.
//
// This is what the live UI calls:
//   * Story 6.9's "New evaluation" CTA  → `startEvaluationDraftNow({ clientId })`
//   * Story 6.5's scoring save-on-blur  → `saveEvaluationDraftNow({ evalId, version, data })`
// 7.8 ships the producer + this wiring + unit tests; binding them to those live call-sites is the
// consumer stories' job (see the story's scope boundary — the same producer/consumer split as 7.4).

import { apiFetch, ApiError } from "./api-client";
import { triggerMainThreadDrain } from "./drain-runner";
import {
  saveEvaluationDraft,
  startEvaluationDraft,
  type ProducerDeps,
  type SaveDraftInput,
  type SaveDraftOutcome,
  type StartDraftInput,
} from "./draft-producer";
import { db } from "./idb-schema";
import { readOwnerIdentity } from "./owner-scope";
// Story 7.7: the real `isEffectivelyOnline()` (a `meta.force_offline` short-circuit + a
// `GET /health` probe ANDed with the last drain outcome) now lives in `online-status.ts`. We
// import it for `buildProducerDeps().isOnline` AND re-export it so existing importers keep the
// same `./draft-producer-runner` entry point. The producer consumes `isOnline` via DI and
// `ProducerDeps.isOnline` already accepts the async form, so the save-routing behaviour is
// unchanged — only the implementation behind the seam swapped.
import { isEffectivelyOnline } from "./online-status";

export { isEffectivelyOnline } from "./online-status";

/** PHI-free `POST /evaluations/{id}/save-draft` response (we consume `version` + `state`). */
interface SaveDraftResponse {
  id: string;
  version: number;
  state: string;
  updated_at?: string;
}

/** Build the real producer deps over the npm `db` singleton, `apiFetch`, and the 7.4 drain trigger. */
export function buildProducerDeps(): ProducerDeps {
  return {
    db,
    isOnline: isEffectivelyOnline,
    saveOnline: async ({ evalId, version, data }) => {
      const res = await apiFetch<SaveDraftResponse>(`/evaluations/${evalId}/save-draft`, {
        method: "POST",
        body: data,
        headers: { "X-Client-Version": String(version) },
      });
      // Response-shape guard (7.8 review deferral): a 2xx body missing/garbling `version` or
      // `state` must NOT be re-anchored into the `drafts` row — an `undefined`/`NaN` version
      // poisons the next X-Client-Version into a spurious 409/422 loop. Throwing here surfaces
      // as an ordinary save failure (not `isOfflineError`, so no enqueue) and leaves the local
      // row at its last known-good version. Twin of `checkedSuccess` in `drain-runner.ts`,
      // which gates the same body on the queued-replay leg — same predicate, keep in sync.
      if (!Number.isInteger(res?.version) || res.version < 0 || typeof res.state !== "string") {
        throw new Error(
          `save-draft returned a malformed body (version=${String(res?.version)}, state=${String(
            res?.state,
          )})`,
        );
      }
      return { version: res.version, state: res.state };
    },
    // A network failure surfaces from `apiFetch` as `ApiError(NETWORK_ERROR, status 0)` — that (and
    // only that) is the "thought-online-but-offline" case that falls back to enqueue. Every other
    // ApiError (409 version conflict, 422, 5xx, …) propagates to the caller / 7.5 resolver.
    isOfflineError: (err) => err instanceof ApiError && err.status === 0,
    // Fire-and-forget: a failed drain leaves the op queued for the next trigger (online/load).
    triggerDrain: () => {
      void triggerMainThreadDrain().catch(() => {});
    },
    // The real `rw` transaction over the two tables the producer writes, so a local draft write +
    // its queue enqueue commit (or roll back) together — same atomicity the drain side uses
    // (`drain-runner.ts` `runAtomic`). Pure tests inject their own (or omit it).
    runAtomic: (fn) => db.transaction("rw", db.drafts, db.outbound_queue, fn),
    now: () => new Date().toISOString(),
    newId: () => crypto.randomUUID(),
    // Story 19.6 Unit E — stamp every INSERTED row with the clinician using this device, so the
    // offline reads on a shared device scope to their own work. Resolved from the durable
    // `meta.owner_identity` key (+ its in-memory mirror); read OUTSIDE `runAtomic` by the core,
    // since `meta` is not part of that transaction.
    owner: readOwnerIdentity,
  };
}

/** Start a new evaluation draft with the real deps (the bound API for Story 6.9's CTA). */
export function startEvaluationDraftNow(input: StartDraftInput): Promise<{ id: string }> {
  return startEvaluationDraft(buildProducerDeps(), input);
}

/** Save a draft edit with the real deps (the bound API for Story 6.5's save-on-blur). */
export function saveEvaluationDraftNow(input: SaveDraftInput): Promise<SaveDraftOutcome> {
  return saveEvaluationDraft(buildProducerDeps(), input);
}
